import React from "react";

// Shared receipt/invoice layout helpers. All numeric inputs to this layout originate from the canonical
// "checkout totals" order of operations defined on `Checkout.recalculateTotals` (frontend, JSDoc) and
// `\Abstracts\Utilities::buildSectionFromLines` (backend). Keep formatters and reconciliation logic here
// consistent with that spec so cart, receipt, invoice, and reports all agree.

/** Matches Checkout `renderInvoiceTaxSection` grid (3 columns, colons align in col 1). */
export const receiptInvoiceGrid = {
	display: "grid",
	gridTemplateColumns: "minmax(11.5rem, max-content) minmax(0, 1fr) max-content",
	columnGap: "0.45rem",
	alignItems: "baseline",
	width: "100%",
	fontSize: "0.95em",
	lineHeight: 1.45,
	padding: "2px 0",
};

export const receiptInvoiceLabel = {
	gridColumn: 1,
	textAlign: "right",
	justifySelf: "end",
	minWidth: 0,
};

export const receiptInvoiceMid = {
	gridColumn: 2,
	textAlign: "left",
	whiteSpace: "nowrap",
	minWidth: 0,
	wordBreak: "break-word",
};

export const receiptInvoiceAmt = {
	gridColumn: 3,
	whiteSpace: "nowrap",
	textAlign: "right",
	justifySelf: "end",
};

/**
 * Transaction / layaway sale snapshot root `{ v, original, current, meta }` (v3+) or null if not object-shaped.
 * @param {string|object|null|undefined} taxJson
 */
export function parseTaxSnapshotFromTransactionJson(taxJson) {
	if (taxJson == null || taxJson === "") return null;
	try {
		const obj = typeof taxJson === "string" ? JSON.parse(taxJson) : taxJson;
		if (obj && typeof obj === "object" && obj.original != null && obj.current != null) {
			return obj;
		}
		return null;
	} catch (e) {
		return null;
	}
}

/**
 * Total cash returned to the customer (`meta.cumulative_cash_refunded`), if present.
 * @param {string|object|null|undefined} taxJson
 * @returns {number|null}
 */
export function cumulativeCashRefundedFromTaxJson(taxJson) {
	const snap = parseTaxSnapshotFromTransactionJson(taxJson);
	if (!snap || !snap.meta) return null;
	const n = Number(snap.meta.cumulative_cash_refunded);
	return Number.isFinite(n) ? n : null;
}

/**
 * Original sale tax total from snapshot `original.tax_total`, else fallback (e.g. header).
 * @param {string|object|null|undefined} taxJson
 * @param {number|string} fallbackTax
 */
export function originalTaxTotalFromTaxJson(taxJson, fallbackTax) {
	const snap = parseTaxSnapshotFromTransactionJson(taxJson);
	if (snap && snap.original && snap.original.tax_total != null) {
		const n = Number(snap.original.tax_total);
		if (Number.isFinite(n)) return n;
	}
	return Number(fallbackTax) || 0;
}

/**
 * Bucket rows for receipts/UI: snapshot `original` or `current` buckets, or legacy array (e.g. layaways).
 * @param {string|object|array|null|undefined} taxJson
 * @param {'original'|'current'} [section]
 * @returns {object[]}
 */
export function parseTransactionTaxJson(taxJson, section = "original") {
	const snap = parseTaxSnapshotFromTransactionJson(taxJson);
	const sec = section === "current" ? "current" : "original";
	if (snap && snap[sec] && Array.isArray(snap[sec].buckets)) {
		return snap[sec].buckets || [];
	}
	if (taxJson == null || taxJson === "") return [];
	if (Array.isArray(taxJson)) return taxJson;
	if (typeof taxJson === "object" && !snap) {
		const vals = Object.values(taxJson);
		return Array.isArray(vals) ? vals : [];
	}
	try {
		const x = typeof taxJson === "string" ? JSON.parse(taxJson) : taxJson;
		return Array.isArray(x) ? x : [];
	} catch (e) {
		return [];
	}
}

/**
 * Build bucket rows with tax dollars; optionally reconcile last bucket to taxAmountTotal (legacy header lock).
 * @param {object|null} [opts.snap] full tax snapshot when available
 * @param {'original'|'current'} [opts.section]
 * @returns {{ buckets: Array<{group: string, rate: number, amount: number, quantity: number, tax: number}>, hasDetail: boolean }}
 */
export function normalizeTaxBucketsFromJson(parsed, taxAmountTotal, opts = null) {
	const taxTotal = Number(taxAmountTotal) || 0;
	if (!parsed.length) return { buckets: [], hasDetail: false };

	const hasAmount = parsed.some(
		(r) => r != null && r.amount != null && r.rate != null && String(r.amount) !== ""
	);
	if (!hasAmount) return { buckets: [], hasDetail: false };

	let rows = parsed.map((r) => {
		const amount = Number(r.amount) || 0;
		const rate = Number(r.rate) || 0;
		let tax =
			r.tax != null && r.tax !== ""
				? Number(r.tax)
				: Math.round((amount * rate) / 100 * 100) / 100;
		return {
			group: r.group || "Default",
			rate,
			amount,
			quantity: r.quantity != null ? r.quantity : 0,
			tax,
		};
	});
	const sec = opts && opts.section === "current" ? "current" : "original";
	const pol = opts && opts.snap && opts.snap.meta && opts.snap.meta.reconcile_policy;
	const perBucketNatural =
		(opts && opts.snap && Number(opts.snap.v) >= 4) ||
		(pol && pol[sec] === "per_bucket_natural");
	if (!perBucketNatural) {
		const sum = rows.reduce((s, row) => s + row.tax, 0);
		const diff = Math.round((taxTotal - sum) * 100) / 100;
		if (diff !== 0 && rows.length) {
			const last = rows[rows.length - 1];
			last.tax = Math.round((last.tax + diff) * 100) / 100;
		}
	}
	return { buckets: rows, hasDetail: true };
}

/**
 * Collapse tax bucket rows that share the same rate (4 decimal places) for printed receipts — one line per %.
 * @param {Array<{group?: string, rate: number, amount: number, tax: number, quantity?: number}>} rows
 */
function mergeTaxBucketRowsByRateForReceipt(rows) {
	if (!Array.isArray(rows) || rows.length <= 1) {
		return rows;
	}
	const map = new Map();
	const order = [];
	for (const row of rows) {
		const rate = Number(row.rate) || 0;
		const key = rate.toFixed(4);
		if (!map.has(key)) {
			order.push(key);
			map.set(key, { rate, amount: 0, tax: 0, groups: new Set() });
		}
		const agg = map.get(key);
		agg.amount += Number(row.amount) || 0;
		agg.tax += Number(row.tax) || 0;
		if (row.group != null && String(row.group) !== "") {
			agg.groups.add(String(row.group));
		}
	}
	return order.map((key) => {
		const agg = map.get(key);
		const groups = [...agg.groups];
		const group = groups.length === 1 ? groups[0] : "Tax";
		return {
			group,
			rate: agg.rate,
			amount: Math.round(agg.amount * 100) / 100,
			tax: Math.round(agg.tax * 100) / 100,
		};
	});
}

/**
 * Jurisdiction brackets from snapshot meta (excludes Firearm Tax).
 * @param {object|null|undefined} meta
 * @returns {Array<{name: string, rate: number}>}
 */
export function jurisdictionTaxRatesFromMeta(meta) {
	if (!meta || !Array.isArray(meta.location_tax_rates)) {
		return [];
	}
	return meta.location_tax_rates
		.filter((r) => r && r.name !== "Firearm Tax" && Number(r.rate) > 0)
		.map((r) => ({ name: String(r.name), rate: Number(r.rate) }));
}

/**
 * @param {Array<{name: string, rate: number}>} rates
 */
export function combinedRateFromLocationTaxRates(rates) {
	let sum = 0;
	for (const r of rates) {
		sum += Number(r.rate) || 0;
	}
	return Math.round(sum * 100) / 100;
}

/**
 * Per-bracket tax from taxable base; last bracket absorbs penny diff to bucketTax.
 * @param {Array<{name: string, rate: number}>} rates
 */
export function buildReceiptTaxBracketLines(taxableBase, bucketTax, rates) {
	if (taxableBase <= 0 || bucketTax < 0.01 || !rates.length) {
		return [];
	}
	const lines = rates.map((r) => ({
		name: r.name,
		rate: r.rate,
		tax: Math.round((taxableBase * r.rate) / 100 * 100) / 100,
	}));
	const sum = lines.reduce((s, row) => s + row.tax, 0);
	const diff = Math.round((bucketTax - sum) * 100) / 100;
	if (diff !== 0 && lines.length) {
		const last = lines.length - 1;
		lines[last] = { ...lines[last], tax: Math.round((lines[last].tax + diff) * 100) / 100 };
	}
	return lines;
}

export function bucketRateMatchesJurisdiction(bucketRate, meta) {
	const jurisdictionRates = jurisdictionTaxRatesFromMeta(meta);
	if (!jurisdictionRates.length) {
		return false;
	}
	const combined = combinedRateFromLocationTaxRates(jurisdictionRates);
	return Math.abs(Number(bucketRate) - combined) <= 0.0001;
}

export function locationDefaultRateFromObject(location) {
	if (!location) return 0;
	const city = Number(location.tax_rate_city ?? location.TaxRateCity ?? 0) || 0;
	const county = Number(location.tax_rate_county ?? location.TaxRateCounty ?? 0) || 0;
	const special = Number(location.tax_rate_special ?? location.TaxRateSpecial ?? 0) || 0;
	const state = Number(location.tax_rate_state ?? location.TaxRateState ?? 0) || 0;
	return city + county + special + state;
}

/**
 * Receipt / transaction summary: tax lines + total tax row (multi-bucket).
 * @param {object} p
 * @param {string} [p.taxJson] transaction.tax_json
 * @param {number|string} p.taxAmount
 * @param {number} [p.taxableMerchandiseApprox] middle column base for single-rate fallback
 * @param {number} [p.blendedRatePercent] for single-rate fallback when no buckets
 * @param {string|number} [p.fontSize] optional (e.g. Receipt_Custom_183 preview)
 * @param {'original'|'current'} [p.taxSection] receipt/transaction view uses `original` (sale-time breakdown)
 */
export function ReceiptTaxSection({
	taxJson,
	taxAmount,
	taxableMerchandiseApprox,
	blendedRatePercent,
	fontSize,
	defaultRatePercent,
	taxSection = "original",
	showTaxBreakdown,
}) {
	const snap = parseTaxSnapshotFromTransactionJson(taxJson);
	const meta = snap && snap.meta ? snap.meta : null;
	const sec = taxSection === "current" ? "current" : "original";
	const tax =
		snap && snap[sec]
			? Number(snap[sec].tax_total ?? taxAmount) || 0
			: Number(taxAmount) || 0;
	const parsed = parseTransactionTaxJson(taxJson, sec);
	const { buckets, hasDetail } = normalizeTaxBucketsFromJson(parsed, tax, { snap, section: sec });

	const liWrap = fontSize ? { listStyle: "none", marginLeft: 0, fontSize } : { listStyle: "none", marginLeft: 0 };
	const gridStyle = fontSize ? { ...receiptInvoiceGrid, fontSize } : receiptInvoiceGrid;
	const bracketIndent = { paddingLeft: "1rem" };

	const defaultRate = Number(defaultRatePercent);
	const hasDefault = Number.isFinite(defaultRate);
	const defaultBuckets = hasDefault
		? buckets.filter((row) => Math.abs(Number(row.rate) - defaultRate) <= 0.0001)
		: [];
	const customBuckets = hasDefault
		? buckets.filter((row) => Math.abs(Number(row.rate) - defaultRate) > 0.0001)
		: buckets;

	let displayBuckets = [];
	if (hasDefault) {
		const defaultAmount = defaultBuckets.reduce((s, row) => s + Number(row.amount || 0), 0);
		const defaultTax = Math.round(defaultBuckets.reduce((s, row) => s + Number(row.tax || 0), 0) * 100) / 100;
		if (defaultAmount > 0 || (customBuckets.length === 0 && tax > 0)) {
			displayBuckets.push({
				group: "Default",
				rate: defaultRate,
				amount: defaultAmount > 0 ? defaultAmount : (taxableMerchandiseApprox != null ? Number(taxableMerchandiseApprox) : 0),
				tax: defaultAmount > 0 ? defaultTax : tax,
			});
		}
		displayBuckets = displayBuckets.concat(customBuckets);
	} else {
		displayBuckets = customBuckets;
	}
	displayBuckets = mergeTaxBucketRowsByRateForReceipt(displayBuckets);

	const jurisdictionRates = jurisdictionTaxRatesFromMeta(meta);

	if (hasDetail && displayBuckets.length > 0) {
		return (
			<>
				{displayBuckets.map((row, idx) => {
					const rowRate = Number(row.rate);
					const rowAmount = Number(row.amount);
					const rowTax = Number(row.tax);
					const showBreakdown =
						showTaxBreakdown &&
						jurisdictionRates.length > 0 &&
						bucketRateMatchesJurisdiction(rowRate, meta);
					const bracketLines = showBreakdown
						? buildReceiptTaxBracketLines(rowAmount, rowTax, jurisdictionRates)
						: [];

					return (
						<li key={`r-tax-${idx}-${row.rate}-${row.amount}`} style={liWrap}>
							{showBreakdown ? (
								<div style={gridStyle}>
									<span style={{ gridColumn: "1 / 3" }}>
										<strong>
											Tax Breakdown ({rowRate.toFixed(3)}% on ${rowAmount.toFixed(2)}):
										</strong>
									</span>
									<span style={receiptInvoiceAmt}>
										<strong>$ {rowTax.toFixed(2)}</strong>
									</span>
									{bracketLines.map((br, brIdx) => (
										<React.Fragment key={`br-${idx}-${brIdx}`}>
											<span style={{ ...receiptInvoiceLabel, ...bracketIndent }}>
												{br.name} ({Number(br.rate).toFixed(4)}%):
											</span>
											<span style={receiptInvoiceMid} />
											<span style={receiptInvoiceAmt}>$ {br.tax.toFixed(2)}</span>
										</React.Fragment>
									))}
								</div>
							) : (
								<div style={gridStyle}>
									<span style={receiptInvoiceLabel}>
										Tax ({rowRate.toFixed(3)}% on ${rowAmount.toFixed(2)}):
									</span>
									<span style={receiptInvoiceMid} />
									<span style={receiptInvoiceAmt}>$ {rowTax.toFixed(2)}</span>
								</div>
							)}
						</li>
					);
				})}
				{displayBuckets.length > 1 && (
					<li style={{ ...liWrap, marginTop: "4px", fontWeight: 600 }}>
						<div style={gridStyle}>
							<span style={receiptInvoiceLabel}>Total tax:</span>
							<span style={receiptInvoiceMid} />
							<span style={receiptInvoiceAmt}>$ {tax.toFixed(2)}</span>
						</div>
					</li>
				)}
			</>
		);
	}

	const merch = taxableMerchandiseApprox != null ? Number(taxableMerchandiseApprox) : 0;
	const rate =
		blendedRatePercent != null && Number.isFinite(Number(blendedRatePercent))
			? Number(blendedRatePercent)
			: merch > 0
				? (tax / merch) * 100
				: 0;

	if (tax > 0) {
		return (
			<li style={liWrap}>
				<div style={gridStyle}>
					<span style={receiptInvoiceLabel}>Tax:</span>
					<span style={receiptInvoiceMid}>
						${merch.toFixed(2)} @ {rate.toFixed(3)}%
					</span>
					<span style={receiptInvoiceAmt}>$ {tax.toFixed(2)}</span>
				</div>
			</li>
		);
	}

	return (
		<li style={liWrap}>
			<div style={gridStyle}>
				<span
					style={{
						gridColumn: "1 / -1",
						textAlign: "right",
						width: "100%",
					}}
				>
					Tax $0.00:
				</span>
			</div>
		</li>
	);
}

/**
 * One summary row: label (col1) | optional mid | right column (col3).
 * @param {React.ReactNode} [props.right] — if set, used instead of `amount` string
 */
export function ReceiptSummaryRow({ label, mid, amount, right, amountStyle, fontSize, strong }) {
	const liWrap = fontSize ? { listStyle: "none", marginLeft: 0, fontSize } : { listStyle: "none", marginLeft: 0 };
	const gridStyle = fontSize ? { ...receiptInvoiceGrid, fontSize } : receiptInvoiceGrid;
	const Amt = strong ? "strong" : "span";
	const rightNode = right != null ? right : amount;
	return (
		<li style={liWrap}>
			<div style={gridStyle}>
				<span style={receiptInvoiceLabel}>{label}</span>
				<span style={receiptInvoiceMid}>{mid || ""}</span>
				<Amt style={{ ...receiptInvoiceAmt, ...amountStyle }}>{rightNode}</Amt>
			</div>
		</li>
	);
}
